/**
 * icons.ts — the Atlas UI icon set: hand-tuned stroke glyphs on a 24×24 grid
 * (1.8 px stroke, round caps/joins, currentColor), replacing every emoji icon.
 *
 * ONE source of truth, two renderers:
 *   makeIcon(name, size)     → an inline <svg> element (DOM UI — menu bar,
 *                              window title bars). Built via createElementNS,
 *                              no innerHTML.
 *   drawIcon(ctx, name, ...) → the same paths rasterized into a 2D canvas via
 *                              Path2D (the VR panel — canvas-textured quad).
 */

const VIEW = 24;

/** Icon path data (SVG path strings, 24×24 box, stroke-drawn). */
export const ICON_PATHS: Record<string, string[]> = {
  // ── Panels / features ────────────────────────────────────────────────────────
  help: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18',
    'M9.2 9.2a2.8 2.8 0 0 1 5.44.93c0 1.87-2.64 2.37-2.64 3.87',
    'M12 17.2h.01',
  ],
  gear: [
    'M12 9.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6',
    'M19.2 12a7.2 7.2 0 0 0-.14-1.4l2.1-1.63-2-3.46-2.47 1a7.3 7.3 0 0 0-2.43-1.41L13.9 2.5h-3.8l-.36 2.6a7.3 7.3 0 0 0-2.43 1.4l-2.47-1-2 3.47 2.1 1.63a7.2 7.2 0 0 0 0 2.8l-2.1 1.63 2 3.46 2.47-1a7.3 7.3 0 0 0 2.43 1.41l.36 2.6h3.8l.36-2.6a7.3 7.3 0 0 0 2.43-1.4l2.47 1 2-3.47-2.1-1.63c.09-.45.14-.92.14-1.4z',
  ],
  globe: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18',
    'M3 12h18',
    'M12 3c2.33 2.36 3.5 5.36 3.5 9s-1.17 6.64-3.5 9c-2.33-2.36-3.5-5.36-3.5-9S9.67 5.36 12 3',
  ],
  map: [
    'M3.5 6.2l5.7-2.2 5.6 2.2 5.7-2.2v13.8l-5.7 2.2-5.6-2.2-5.7 2.2z',
    'M9.2 4v13.8',
    'M14.8 6.2V20',
  ],
  swap: [
    'M16.5 2.5l4 4-4 4',
    'M3.5 10.5v-1a3 3 0 0 1 3-3h14',
    'M7.5 21.5l-4-4 4-4',
    'M20.5 13.5v1a3 3 0 0 1-3 3h-14',
  ],
  passport: [
    'M4.5 4.5h15a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2',
    'M8.75 9a2 2 0 1 0 0 4 2 2 0 0 0 0-4',
    'M5.5 16.4c.55-1.9 5.95-1.9 6.5 0',
    'M14.5 9.5h5',
    'M14.5 13h5',
  ],
  compass: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18',
    'M15.7 8.3l-2.1 5.3-5.3 2.1 2.1-5.3z',
  ],
  orbit: [
    'M12 8.75a3.25 3.25 0 1 0 0 6.5 3.25 3.25 0 0 0 0-6.5',
    'M18.6 6.55c1.9 1 3 2.2 2.85 3.4-.3 2.3-5.2 3.6-11 2.9-5.7-.7-10.1-3.1-9.8-5.4.14-1.2 1.5-2.05 3.6-2.5',
    'M18.9 17h.01',
  ],
  build: [
    'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
  ],
  forge: [
    'M15.2 12.2l-8 8a2 2 0 0 1-2.83-2.83l8-8',
    'M17.3 4.1l2.6 2.6a2 2 0 0 1 0 2.83l-1.8 1.8-5.43-5.43 1.8-1.8a2 2 0 0 1 2.83 0',
  ],
  logic: [
    'M6 3.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5',
    'M6 15.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5',
    'M18 9.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5',
    'M6 8.25v7.5',
    'M8.1 6.9l7.8 4.2',
    'M8.1 17.1l7.8-4.2',
  ],
  code: [
    'M8 7.5l-4.5 4.5L8 16.5',
    'M16 7.5l4.5 4.5L16 16.5',
    'M13.2 4.5l-2.4 15',
  ],
  chart: [
    'M4.5 19.5v-8',
    'M10.25 19.5v-15',
    'M16 19.5v-5.5',
    'M2.5 19.5h19',
  ],
  bug: [
    'M9.2 8.5V6.3a2.8 2.8 0 0 1 5.6 0v2.2',
    'M12 8.5a4.3 4.3 0 0 1 4.3 4.3v2.4a4.3 4.3 0 0 1-8.6 0v-2.4A4.3 4.3 0 0 1 12 8.5',
    'M3.2 13.5h4.5',
    'M16.3 13.5h4.5',
    'M4.6 19.4l3.1-2.1',
    'M19.4 19.4l-3.1-2.1',
    'M4.6 7.9l3.1 2',
    'M19.4 7.9l-3.1 2',
  ],
  // ── Window chrome + VR glyphs ────────────────────────────────────────────────
  close: ['M6.5 6.5l11 11', 'M17.5 6.5l-11 11'],
  shade: ['M5.5 12h13'],
  opacity: ['M12 3.2s5.8 6.2 5.8 9.8a5.8 5.8 0 0 1-11.6 0c0-3.6 5.8-9.8 5.8-9.8', 'M8 13.4c0 2.2 1.8 4 4 4'],
  grip: ['M19.5 13.5l-6 6', 'M19.5 8.5l-11 11'],
  menu: ['M4.5 7h15', 'M4.5 12h15', 'M4.5 17h15'],
  home: ['M4 11.2l8-6.7 8 6.7', 'M6.2 9.8V20h11.6V9.8', 'M10 20v-5.5h4V20'],
  exit: ['M9.5 21h-4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 16.5l4.5-4.5L16 7.5', 'M20.5 12h-11'],
  pin: ['M12 21.3S5.2 15.3 5.2 10.4a6.8 6.8 0 0 1 13.6 0c0 4.9-6.8 10.9-6.8 10.9', 'M12 8.4a2 2 0 1 0 0 4 2 2 0 0 0 0-4'],
  arrow: ['M4.5 12h15', 'M13.5 6l6 6-6 6'],
  diamond: ['M12 3.2L19.5 12 12 20.8 4.5 12z'],
  star: ['M12 3.4l2.6 5.6 6 .7-4.4 4.1 1.2 5.9L12 16.7l-5.4 3-1.2-5.9L1 9.7l6-.7z'],
  clock: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18', 'M12 7.5V12l3.2 2'],
  people: [
    'M9 9.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
    'M2.8 20c.5-4 3-6 6.2-6s5.7 2 6.2 6',
    'M16.2 3.9a3 3 0 0 1 0 5.2',
    'M17.5 14.3c2.2.7 3.4 2.6 3.7 5.7',
  ],
  // ── Legacy-panel content glyphs (the emoji sweep) ────────────────────────────
  box: [
    'M3.5 7.5l8.5-4 8.5 4v9l-8.5 4-8.5-4z',
    'M3.5 7.5l8.5 4 8.5-4',
    'M12 11.5v9',
  ],
  brick: [
    'M3 9h18v10.5H3z',
    'M3 14.25h18',
    'M8 9v5.25',
    'M16 9v5.25',
    'M12 14.25v5.25',
    'M6.5 4.5h11l1.5 4.5H5z',
  ],
  bank: [
    'M3.5 9.5L12 4l8.5 5.5',
    'M5 9.5h14',
    'M6.5 9.5V17',
    'M12 9.5V17',
    'M17.5 9.5V17',
    'M4 17h16v2.5H4z',
  ],
  trophy: [
    'M7.5 4h9v5.5a4.5 4.5 0 0 1-9 0z',
    'M7.5 6H4.5a3 3 0 0 0 3 4.4',
    'M16.5 6h3a3 3 0 0 1-3 4.4',
    'M12 14v3',
    'M8.5 20h7',
    'M10 17h4v3h-4z',
  ],
  gamepad: [
    'M7 8h10a4.5 4.5 0 0 1 4.4 5.5l-.9 3.6a2.3 2.3 0 0 1-4 .9L14.6 16H9.4l-1.9 2a2.3 2.3 0 0 1-4-.9l-.9-3.6A4.5 4.5 0 0 1 7 8',
    'M8.5 11v3',
    'M7 12.5h3',
    'M15.5 11.4h.01',
    'M17.4 13.2h.01',
  ],
  lock: [
    'M6.5 11h11a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 18.5v-6A1.5 1.5 0 0 1 6.5 11',
    'M8.5 11V8a3.5 3.5 0 0 1 7 0v3',
    'M12 14.4v2.2',
  ],
  unlock: [
    'M6.5 11h11a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 18.5v-6A1.5 1.5 0 0 1 6.5 11',
    'M8.5 11V8a3.5 3.5 0 0 1 6.8-1.2',
    'M12 14.4v2.2',
  ],
  ticket: [
    'M4 8.5h16v3a1.75 1.75 0 0 0 0 3.5v3H4v-3a1.75 1.75 0 0 0 0-3.5z',
    'M13.5 8.5v1.6',
    'M13.5 12.2v1.6',
    'M13.5 15.9v1.6',
  ],
  check: ['M4.5 12.5l5 5 10-11'],
};

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Build an inline stroke-icon <svg> (currentColor — tint via CSS color). */
export function makeIcon(name: string, size = 16): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${VIEW} ${VIEW}`);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  for (const d of ICON_PATHS[name] ?? ICON_PATHS.help) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
}

/**
 * Static SVG markup for innerHTML templates (legacy panels build market-item
 * lists via template strings). STATIC content only — never interpolate user
 * input into this.
 */
export function iconMarkup(name: string, size = 13): string {
  const paths = (ICON_PATHS[name] ?? ICON_PATHS.help)
    .map((d) => `<path d="${d}"/>`)
    .join('');
  return `<svg viewBox="0 0 ${VIEW} ${VIEW}" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align:-2px;margin-right:3px">${paths}</svg>`;
}

/** Rasterize the same icon into a 2D canvas (the VR panel path). */
export function drawIcon(
  g: CanvasRenderingContext2D,
  name: string,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const paths = ICON_PATHS[name] ?? ICON_PATHS.help;
  g.save();
  g.translate(x, y);
  g.scale(size / VIEW, size / VIEW);
  g.strokeStyle = color;
  g.lineWidth = 1.8;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const d of paths) g.stroke(new Path2D(d));
  g.restore();
}
